import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;
const USER_CACHE_KEY = 've_user';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_CACHE_KEY)); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem("ve_token"));
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async (t) => {
    try {
      const r = await axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
      setUser(r.data);
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(r.data));
    } catch (err) {
      // Logout solo per errori di autenticazione (token non valido/scaduto)
      // Errori di rete (offline) → mantieni la sessione con i dati cachati
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("ve_token");
        localStorage.removeItem(USER_CACHE_KEY);
        setToken(null);
        setUser(null);
      }
      // Nessuna connessione → setUser resta con i dati cachati già inizializzati
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (token) {
      fetchMe(token);
    } else {
      setLoading(false);
    }
  }, [token, fetchMe]);

  const saveSession = (data) => {
    localStorage.setItem("ve_token", data.token);
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
  };

  const refreshUser = () => token && fetchMe(token);

  const logout = () => {
    axios.post(`${API}/api/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    localStorage.removeItem("ve_token");
    localStorage.removeItem(USER_CACHE_KEY);
    setToken(null);
    setUser(null);
  };

  const subscription = user?.subscription || "free";
  const isPremium = subscription === "premium" || subscription === "business";
  const isBusiness = subscription === "business";

  return (
    <AuthContext.Provider value={{ user, token, loading, saveSession, logout, refreshUser, subscription, isPremium, isBusiness }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
